import React, { useState, useEffect, cloneElement, useRef } from "react";
import { useMutation } from "@apollo/react-hooks";
import { useHistory, useParams, useLocation } from "react-router-dom";
import { useSnackbar } from "notistack";
import { Box, Paper } from "@material-ui/core";
import { Loader, Sheet, SheetContent } from "../../components";
import * as isEmpty from "lodash.isempty";
import {
  GGV_POST_FINALIZE_REGISTRATION,
  GGV_POST_FINALIZE_PRE_REGISTRATION,
  POST_FINALIZE_REGISTRATION as GGT_POST_FINALIZE_REGISTRATION
} from "../../queries";
import {
  CONSUMER_SESSION_APPOINTMENT,
  ROUTE_PATHS,
  BRAND_GGV,
  BRAND_GGT,
  CONSUMER_SESSION_VERIFICATION_TOKEN,
  SERVICE_COVID_19_TEST,
  SERVICE_COVID_19_VAX,
  SERVICE_COVID_19_VACCINE_VAX_TYPES,
  COUNTRIES_CURRENCY
} from "../../constants";
import {
  SCHEDULE_SCREEN_COMPONENTS,
  SCHEDULE_SCREEN_CONFIG
} from "./constants";
import { useConsumerAuth } from "../../hooks";
import { useLanguages } from "../../contexts";
import PropTypes from "prop-types";
import { useStripe } from "@stripe/react-stripe-js";
import { getNewScreenFlow, isTestService } from "../../utils";

const INJECT_SCREENS = [
  {
    screen_name: "occupation",
    prev_screen: "time"
  }
];

const FINALIZE_REGISTRATION_ERROR =
  "Unable to schedule your appointment. Please try again.";

const Wrapper = ({ hideWrapper = false, children }) =>
  hideWrapper ? (
    children
  ) : (
    <Paper>
      <Box p={2}>{children}</Box>
    </Paper>
  );

const Schedule = ({ brand = BRAND_GGT }) => {
  const stripe = useStripe();
  const history = useHistory();
  const location = useLocation();
  const { step } = useParams();
  const { enqueueSnackbar } = useSnackbar();
  const { phone_number, token, updateSession } = useConsumerAuth();
  const { language } = useLanguages();

  const [currentScreen, setCurrentScreen] = useState();
  const [screenFlow, setScreenFlow] = useState();
  const [screenFlowConfig, setScreenFlowConfig] = useState();
  const [scheduleData, setScheduleData] = useState({});
  const [drawerClearance, setDrawerClearance] = useState(0);

  const skipScreenCount = useRef(0);

  /**
   * Post final pre-registration
   */
  const [
    postFinalizePreRegistration,
    { loading: isSubmittingPreReg }
  ] = useMutation(GGV_POST_FINALIZE_PRE_REGISTRATION, {
    onCompleted: ({ registration }) => {
      if (registration && registration.status === "success") {
        return history.push(`/${brand}/schedule/pre-register/success`);
      } else {
        enqueueSnackbar("Could not pre-register", {
          variant: "error"
        });
      }
    }
  });

  /**
   * get correct query depending on brand
   *
   * @param {string} brand
   */
  const getFinalizeRegistrationQuery = brand => {
    switch (brand) {
      case BRAND_GGV:
        return GGV_POST_FINALIZE_REGISTRATION;
      default:
        return GGT_POST_FINALIZE_REGISTRATION;
    }
  };

  /**
   * Post final registration
   */
  const [postFinalizeRegistration, { loading: isSubmitting }] = useMutation(
    getFinalizeRegistrationQuery(brand),
    {
      onCompleted: ({ registration }) => {
        if (registration && registration.status === "success") {
          // Format DOB used for appointment view
          const dob = scheduleData.patientDetails.dob.replace(/-/g, "");

          // Save appointment to session
          sessionStorage.setItem(
            CONSUMER_SESSION_APPOINTMENT,
            JSON.stringify({ ...registration, dob })
          );

          // Remove verification token
          sessionStorage.removeItem(CONSUMER_SESSION_VERIFICATION_TOKEN);

          // Update session token with refreshed token
          if (registration.session_token)
            updateSession({ session_token: registration.session_token });

          if (registration.payment_checkout_session) {
            if (
              registration.payment_checkout_session !==
              "_TEST_PAYMENT_CHECKOUT_SESSION_"
            ) {
              return stripe.redirectToCheckout({
                sessionId: registration.payment_checkout_session
              });
            } else {
              return history.push(
                `/appointment/${registration.appointment_id}/pay/success?token=stripe-success-token`
              );
            }
          } else {
            enqueueSnackbar("Appointment scheduled successfully", {
              variant: "success"
            });

            return history.push(`/`);
          }
        } else {
          enqueueSnackbar(FINALIZE_REGISTRATION_ERROR, {
            variant: "error"
          });
        }
      },
      onError: () => {
        enqueueSnackbar(FINALIZE_REGISTRATION_ERROR, { variant: "error" });
      }
    }
  );

  /**
   * Handle irrelevant steps based on configuration
   *
   * @param {string} step
   * @param {object} scheduleData
   * @param {object} screenFlowConfig
   */
  const stepIrrelevant = (step, scheduleData = {}, screenFlowConfig = {}) => {
    // Return if no config found
    if (!screenFlowConfig[step] || !screenFlowConfig[step].ignore) return false;

    const ignoreConditionsMet = screenFlowConfig[step].ignore.filter(rule => {
      const { scheduleStateKey: key, compare = "EQ", value } = rule;

      if (key in scheduleData) {
        switch (compare) {
          case "EQ":
            return scheduleData[key] === value;
          case "NEQ":
            return scheduleData[key] !== value;
          case "IN":
            return scheduleData[key][value] && scheduleData[key][value] === true
              ? true
              : false;
          case "NOT IN":
            return !scheduleData[key][value] ||
              scheduleData[key][value] === false
              ? true
              : false;
          case "GTE":
            return scheduleData[key] >= value;
          case "LT":
            return scheduleData[key] < value;
          default:
            return false;
        }
      }
    });

    return ignoreConditionsMet.length > 0 ? true : false;
  };

  /**
   * Redirect if we have no screens
   */
  useEffect(() => {
    if (screenFlow) return;

    const { state } = location;

    if (!state || !state.screens) {
      history.push(`/`);
    } else {
      const { groupCode } = state;
      const normalizedGroupCode = groupCode.toLowerCase();

      const finalScreens =
        normalizedGroupCode === "_default_" || normalizedGroupCode === "walkin"
          ? getNewScreenFlow([...state.screens], INJECT_SCREENS)
          : state.screens;

      setScreenFlow(finalScreens);
      setScreenFlowConfig(state.screenConfig);
      setScheduleData(scheduleData => ({
        ...scheduleData,
        groupCode: state.groupCode,
        phone_number,
        token,
        pre_register: state.pre_register ? true : false,
        verification_token: state.verification_token,
        location_id: state.location_id || "",
        country: state.country,
        brand
      }));
    }
  }, [location]);

  /**
   * Redirect to first screen when flow changes
   */
  useEffect(() => {
    if (!screenFlow) return;

    const basePath =
      brand === BRAND_GGV
        ? ROUTE_PATHS.GGV.SCHEDULE_BASE
        : ROUTE_PATHS.GGT.SCHEDULE_BASE;

    return history.push(`${basePath}/${screenFlow[0]}`);
  }, [screenFlow]);

  /**
   * Set screen component
   */
  useEffect(() => {
    if (!step) return;

    if (stepIrrelevant(step, scheduleData, SCHEDULE_SCREEN_CONFIG)) {
      if (process.env.REACT_APP_SENTRY_ENV === "development")
        console.log(`Skipping ${step} based on config rules`);

      const nextStepIndex = screenFlow.indexOf(step) + 1;

      // Increment count, so back button ignores skipped screens
      skipScreenCount.current++;

      if (screenFlow[nextStepIndex]) {
        /**
         * Save previous screen to router state for back button
         * Otherwise history.back will keep going back to ignored screen
         */
        return history.push({
          pathname: screenFlow[nextStepIndex],
          state: {
            skipScreenCount: skipScreenCount.current
          }
        });
      } else {
        return setScheduleData(oldData => ({
          ...oldData,
          forceFinish: true
        }));
      }
    } else {
      // Reset screen skip count for default back button behavior
      skipScreenCount.current = 0;
    }

    setCurrentScreen(
      () => SCHEDULE_SCREEN_COMPONENTS.filter(screen => screen.key === step)[0]
    );
  }, [step]);

  /**
   * Debug: State of scheduleData
   */
  useEffect(() => {
    if (process.env.REACT_APP_SENTRY_ENV === "development")
      console.log(`scheduleData...`, scheduleData);
  }, [scheduleData]);

  /**
   * Set next step
   */
  const setNextStep = () => {
    let currentStep = step;

    const nextStepIndex = currentStep ? screenFlow.indexOf(currentStep) + 1 : 0;
    let path = screenFlow[nextStepIndex];

    if (!path) {
      let finalScheduleData = scheduleData;

      /**
       * If we don't have a selected service, set one.
       * selectedServices is set from "covid19-test-type" screen, which is not
       * always shown in the screen flow
       */
      if (!scheduleData.selectedServices) {
        let selectedServices = [];

        if (brand === BRAND_GGT) {
          // Get Covid-19 test services from location services or default to SERVICE_COVID_19_TEST
          selectedServices = Array.isArray(scheduleData.locationServices)
            ? scheduleData.locationServices
                .filter(service =>
                  isTestService([service.service_code || service.service_code])
                )
                .map(service => service.service_code || service.service_code)
            : [SERVICE_COVID_19_TEST];
        } else if (brand === BRAND_GGV) {
          // Get Covid-19 vax services from location services or default to SERVICE_COVID_19_VAX
          selectedServices =
            scheduleData.locationServices &&
            SERVICE_COVID_19_VACCINE_VAX_TYPES[scheduleData.locationServices]
              ? SERVICE_COVID_19_VACCINE_VAX_TYPES[
                  scheduleData.locationServices
                ]
              : [SERVICE_COVID_19_VAX];
        }

        finalScheduleData = {
          ...finalScheduleData,
          selectedServices
        };
      }

      // Set currency if we don't have one
      const currency = scheduleData.currency || COUNTRIES_CURRENCY.US.abbr;

      const registrationData = {
        variables: {
          body: { ...finalScheduleData, language, currency }
        }
      };

      if (scheduleData.pre_register && scheduleData.pre_register === true) {
        postFinalizePreRegistration(registrationData);
      } else {
        postFinalizeRegistration(registrationData);
      }
    } else {
      return history.push(path);
    }
  };

  /**
   * Set next step after updating schedule data
   */
  useEffect(() => {
    if (isEmpty(scheduleData)) return;

    setNextStep();
  }, [scheduleData]);

  /**
   * Update schedule data
   *
   * @param {object} newData
   */
  const updateScheduleData = newScheduleData => {
    setScheduleData(scheduleData => ({
      ...scheduleData,
      ...newScheduleData
    }));
  };

  return (
    <>
      <Sheet
        data-cy="schedule-view"
        style={{ paddingBottom: `${drawerClearance}px` }}
      >
        <SheetContent>
          <Box mb={2}>
            {currentScreen && currentScreen.component ? (
              <Wrapper hideWrapper={!!currentScreen.hidePaperPanel}>
                {cloneElement(currentScreen.component, {
                  setDrawerClearance,
                  updateScheduleData,
                  setNextStep,
                  scheduleData,
                  isSubmitting:
                    isSubmitting || isSubmittingPreReg ? true : false,
                  screenFlowConfig
                })}
              </Wrapper>
            ) : (
              <Loader />
            )}
          </Box>
        </SheetContent>
      </Sheet>
    </>
  );
};

Schedule.propTypes = {
  brand: PropTypes.oneOf([BRAND_GGV, BRAND_GGT])
};

export { Schedule };
